import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import { authApiClient } from '../../services/authApi';
import { triggerDownload } from '../../utils/exportUtils';

// Admin "Unresolved generics" viewer — read-only. Self-contained and self-fetching like
// SubscriptionsTab; `reqIdRef` is the same latest-response-wins guard.

export interface UnresolvedGroup {
  component: string;
  drug_count: number;
  regions: Record<string, number>;
  reasons: string[];
  sample_drug_ids: number[];
}

interface Page {
  total_groups: number;
  groups: UnresolvedGroup[];
}

const REGIONS = ['US', 'EU', 'UK', 'CA', 'AU', 'JP', 'CN'];
const REASONS = ['no_match', 'ambiguous', 'too_many_components', 'empty_generic_name', 'blocked'];
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200; // backend max; the BFF buffers each response, so never one big request

const fmtRegions = (r: Record<string, number>) =>
  Object.entries(r).map(([k, v]) => `${k} ${v}`).join(' · ');

const buildParams = (region: string, reason: string, offset: number, limit: number) => ({
  ...(region ? { region } : {}),
  ...(reason ? { reason } : {}),
  limit,
  offset,
});

/** Fetch every group for the filters, sequentially, 200 at a time. */
export async function fetchAllUnresolved(
  _filters: { region?: string; reason?: string },
  getPage: (offset: number, limit: number) => Promise<Page>,
): Promise<UnresolvedGroup[]> {
  const all: UnresolvedGroup[] = [];
  for (;;) {
    const page = await getPage(all.length, EXPORT_PAGE_SIZE);
    if (page.groups.length === 0) break;
    all.push(...page.groups);
    if (all.length >= page.total_groups) break;
  }
  return all;
}

export const UnresolvedGenericsTab: React.FC = () => {
  const [region, setRegion] = useState('');
  const [reason, setReason] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/generics/unresolved', {
        params: buildParams(region, reason, offset, PAGE_SIZE),
      });
      if (reqIdRef.current !== myId) return;
      setPage(res.data);
    } catch (err: any) {
      if (reqIdRef.current !== myId) return;
      setPage(null);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load unresolved generics');
    } finally {
      if (reqIdRef.current === myId) setLoading(false);
    }
  }, [region, reason, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const groups = await fetchAllUnresolved({ region, reason }, async (off, limit) =>
        (await authApiClient.get('/admin/generics/unresolved', { params: buildParams(region, reason, off, limit) })).data,
      );
      const csv = Papa.unparse(
        groups.map((g) => ({
          component: g.component,
          drug_count: g.drug_count,
          regions: Object.entries(g.regions).map(([k, v]) => `${k}:${v}`).join(';'),
          reasons: g.reasons.join(';'),
          sample_drug_ids: g.sample_drug_ids.join(';'),
        })),
      );
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      triggerDownload(csv, `unresolved-generics-${ts}.csv`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const total = page?.total_groups ?? 0;
  const groups = page?.groups ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <label htmlFor="unresolved-region" className="text-slate-500">Region</label>
        <select id="unresolved-region" value={region}
          onChange={(e) => { setRegion(e.target.value); setOffset(0); }}
          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white">
          <option value="">All</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <label htmlFor="unresolved-reason" className="text-slate-500">Reason</label>
        <select id="unresolved-reason" value={reason}
          onChange={(e) => { setReason(e.target.value); setOffset(0); }}
          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white">
          <option value="">All</option>
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={exportCsv} disabled={exporting}
          className="ml-auto px-3 py-1 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50 disabled:opacity-50">
          Export CSV
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading unresolved generics…</div>}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between gap-4">
          <span>{error}</span>
          <button onClick={load}
            className="px-3 py-1 border border-red-300 rounded text-red-700 text-xs font-medium hover:bg-red-100">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && page && (
        <>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Component</th>
                  <th className="text-left px-4 py-3 font-medium">Drugs</th>
                  <th className="text-left px-4 py-3 font-medium">Regions</th>
                  <th className="text-left px-4 py-3 font-medium">Reasons</th>
                  <th className="text-left px-4 py-3 font-medium">Samples</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No unresolved generics.</td></tr>
                )}
                {groups.map((g) => (
                  <tr key={`${g.component}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{g.component}</td>
                    <td className="px-4 py-3 text-slate-700">{g.drug_count}</td>
                    <td className="px-4 py-3 text-slate-700">{fmtRegions(g.regions)}</td>
                    <td className="px-4 py-3 text-slate-700">{g.reasons.join(', ')}</td>
                    <td className="px-4 py-3">
                      {g.sample_drug_ids.map((id) => (
                        <Link key={id} to={`/drug/${id}`} className="mr-2 text-blue-600 hover:underline">{id}</Link>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}
              className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-50">Prev</button>
            <span>{total === 0 ? 0 : offset + 1}–{offset + groups.length} of {total}</span>
            <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-50">Next</button>
          </div>
        </>
      )}
    </div>
  );
};
